import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLevumiItemGerman } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemGerman.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { LevumiItemCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import '@chancenwerk/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { filter, map, switchMap, takeUntil } from 'rxjs';

@customElement('campus-dialog-edit-levumi-german-item')
export class CampusDialogEditLevumiGermanItem extends ChromiumDialog<void> {
  @state() public levumiGermanItem = new RXController(this, new CampusLevumiItemGerman({}));
  @state() private readonly _itemDirty = new RXController(
    this,
    this.levumiGermanItem.observable.pipe(
      filter(Boolean),
      switchMap(levumiGermanItem => levumiGermanItem.dirty),
    ),
  );
  @state() private readonly _errorMsg = new RXController<string>(this, '');

  @state() private readonly _levumiLevels = new RXController(
    this,
    LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.GERMAN)(CampusLevumiLevel.allFields),
  );

  @state() private readonly _distractorString = new RXController(this, '');

  @state() private readonly _selectedLevel = new RXController<CampusLevumiLevel | undefined>(this, undefined);
  @state() private readonly _selectedCategory = new RXController<LevumiItemCategory | undefined>(this, undefined);
  @state() private readonly _selectedSubCategory = new RXController<LevumiItemCategory | undefined>(this, undefined);

  private readonly _form = new FormController(this, this.levumiGermanItem.observable, {
    success: lesson => this._handleFormSuccess(lesson),
    error: err => this._handleFormError(err),
  });

  static override get element_name() {
    return 'campus-dialog-edit-levumi-german-item';
  }

  constructor() {
    super();
    this.levumiGermanItem.observable
      .pipe(map(item => this._distractorString.next(item.distractors.join(','))))
      .subscribe();
  }

  override connectedCallback() {
    super.connectedCallback();
    this.levumiGermanItem.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(
        e => (this.heading = !e._id ? 'Levumi Deutsch Aufgabe erstellen' : 'Levumi Deutsch Aufgabe bearbeiten'),
      );
    if (this.levumiGermanItem.value.category.split(' - ').length === 2) {
      this._selectedCategory.next(this.levumiGermanItem.value.category.split(' - ')[0] as LevumiItemCategory);
      this._selectedSubCategory.next(this.levumiGermanItem.value.category.split(' - ')[1] as LevumiItemCategory);
    }
  }

  private async _handleFormSuccess(levGerItem: CampusLevumiItemGerman) {
    if (levGerItem.levelID === this._levumiLevels.value?.find(l => l.level === 'N6')?._id) {
      const categoryString = `${this._selectedCategory.value} - ${this._selectedSubCategory.value}`;
      const category = Object.values(LevumiItemCategory).find(c => c === categoryString);
      if (!category) return this._errorMsg.next('Bitte wähle eine Kategorie/Unterkategorie aus');
      levGerItem.category = category;
    }
    if (
      levGerItem.levelID !== this._levumiLevels.value?.find(l => l.level === 'N6')?._id &&
      !this._filterN6Categories(Object.keys(LevumiItemCategory)).find(
        key => LevumiItemCategory[key] === levGerItem.category,
      )
    )
      return this._errorMsg.next('Ungültige Kategorie für dieses Level');
    if (!this._validateDistractors(this._distractorString.value)) return;

    // Possible switch of level reactivity fix
    const newLevel = this._levumiLevels.value?.find(l => l._id === levGerItem.levelID);
    if (newLevel) {
      this.levumiGermanItem.value.level = newLevel;
    }

    levGerItem.distractors = this._distractorString.value.split(',');
    if (!levGerItem._id) await levGerItem.create().catch(e => this._errorMsg.next(e.detail));
    else await levGerItem.save().catch(e => this._errorMsg.next(e.detail));

    void ChromiumSnackbar.show('Aufgabe gespeichert');
    await this.close();
  }
  private _handleFormError(err: ValidationError<any>) {
    this._errorMsg.next(err.message);
  }

  override content() {
    return html`
      <cr-flex space="md" padding="md">
        ${this._errorMsg.value.length
          ? html` <cr-text ${css({ color: 'red' })}>${this._errorMsg.value}</cr-text>`
          : nothing}
        <cr-flex horizontal space="md" crossaxis-end>
          <cr-dropdown-selection
            hide-add-button
            placeholder="Level"
            ${this._form.field('levelID')}
            .selectables=${this._levumiLevels.value?.map(l => l._id)}
            .renderString=${(levelID: string) => this._levumiLevels.value?.find(lev => lev._id === levelID)?.level}
            .renderItem=${(levelID: string) => {
              const level = this._levumiLevels.value?.find(lev => lev._id === levelID);
              if (level?.level === 'unspecified') return;
              return html`
                <cr-flex
                  center
                  crossaxis-center
                  horizontal
                  space="md"
                  padding="md"
                  ${css({ borderTop: `1px solid #f2f2f2` })}
                >
                  <cr-text>${level?.level}</cr-text>
                  <cr-button
                    mode="text"
                    icon="delete"
                    tone="error"
                    @mousedown=${async e => {
                      e.stopPropagation();
                      if (!level) return;
                      await this._deleteLevel(level);
                    }}
                  >
                    Löschen
                  </cr-button>
                </cr-flex>
              `;
            }}
            @value-picked=${(e: ValueEvent<string>) => {
              this._selectedCategory.next(undefined);
              this._selectedSubCategory.next(undefined);
              this._selectedLevel.next(this._levumiLevels.value?.find(l => l._id === e.detail.value));
            }}
            flex
          ></cr-dropdown-selection>

          <cr-button
            icon="add"
            @click=${async e => {
              const levelName = await ChromiumDialog.prompt('Name für das neue Levumi Level eintragen', {
                placeholder: 'Levelname',
              });
              if (!levelName && this._levumiLevels.value?.some(val => val.level === levelName)) return;
              await new CampusLevumiLevel({
                level: levelName,
                subject: LevumiSubject.GERMAN,
              }).create();
              void ChromiumSnackbar.show('Levumi Level ' + levelName + ' wurde für Deutsch gespeichert');
            }}
          >
          </cr-button>
        </cr-flex>
        ${this.levumiGermanItem.value.levelID === this._levumiLevels.value?.find(l => l.level === 'N6')?._id
          ? html`
              <cr-dropdown-selection
                .value=${this._selectedCategory.value}
                hide-add-button
                placeholder="Kategorie"
                .selectables=${this._getUniqueMainCategories(Object.values(LevumiItemCategory))}
                @value-picked=${(e: ValueEvent<LevumiItemCategory>) => {
                  this._selectedCategory.next(e.detail.value);
                  this._selectedSubCategory.next(undefined);
                  this.requestUpdate();
                }}
              >
              </cr-dropdown-selection>
              ${guard([this._selectedCategory.value, this._selectedSubCategory.value], () => {
                return html`
                  <cr-dropdown-selection
                    .value=${this._selectedSubCategory.value}
                    hide-add-button
                    placeholder="Unterkategorie"
                    .selectables=${this._getUniqueSubCategoriesForMainCategory(
                      this._selectedCategory.value ?? '',
                      Object.values(LevumiItemCategory),
                    )}
                    @value-picked=${(e: ValueEvent<LevumiItemCategory>) => {
                      this._selectedSubCategory.next(e.detail.value);
                    }}
                  >
                  </cr-dropdown-selection>
                `;
              })}
            `
          : html`
              <cr-dropdown-selection
                hide-add-button
                placeholder="Kategorie"
                ${this._form.field('category')}
                .selectables=${Array.from(
                  this._filterN6Categories(Object.keys(LevumiItemCategory)).map(key => LevumiItemCategory[key]),
                )}
              >
              </cr-dropdown-selection>
            `}

        <cr-input ${this._form.field('sentence')} placeholder="Aufgabentext: (Bsp: Ich $ gerne Bücher)"></cr-input>
        <cr-input ${this._form.field('answer')} placeholder="Richtige Antwort"></cr-input>
        <cr-input
          @value-picked=${(e: ValueEvent<string>) => {
            this._distractorString.next(e.detail.value);
          }}
          placeholder="Ablenkungen: Apfel,Birne,Auto"
          .value=${this._distractorString.value}
        ></cr-input>
        <cr-flex horizontal crossaxis-center end space="md">
          ${this.levumiGermanItem.value._id
            ? html`
                <campus-delete-button
                  @click=${async () => {
                    if (!(await ChromiumDialog.confirm('Funktion wirklich löschen?'))) return;
                    await this.levumiGermanItem.value.remove();
                    await this.close();
                  }}
                >
                </campus-delete-button>
              `
            : nothing}
          <cr-button @click=${() => this._form.validate()}>
            ${!this.levumiGermanItem.value._id ? html` Erstellen ` : html` Speichern `}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private async _deleteLevel(level: CampusLevumiLevel) {
    const levelIsUsed = await LearningContentAPI.levumi.getLevelCurrentlyInUse(level._id)();
    if (levelIsUsed) {
      if (
        await ChromiumDialog.confirm(
          html`
            <cr-flex center padding="md" space="md" space-between crossaxis-center>
              <cr-h2 ${css({ color: 'red' })}>Achtung!</cr-h2>
              <cr-h3>
                Dieses LevumiLevel wird im Augenblick noch von anderen Aufgaben benutzt. Bei der Löschung werden diese
                Aufgaben zu dem Level 'unspecified' hinzugefügt. Sie sind dann nicht mehr in den jeweiligen Aufgaben
                verfügbar und müssen manuell zu einem Level hinzugefügt werden. Wollen Sie dieses Level wirklich
                löschen?
              </cr-h3>
            </cr-flex>
          `,
          { highlightDecline: true },
        )
      ) {
        const sameLevelAsCurrentItem = this.levumiGermanItem.value.level._id === level._id;
        await LearningContentAPI.levumi.changeLevelToUnspecified(CampusLevumiItemGerman._class, level._id)();
        await level.remove();
        if (sameLevelAsCurrentItem) await this.close();
      }
    } else {
      await level.remove();
    }
  }

  private _filterN6Categories(categories: string[]) {
    const filteredCategories: string[] = [];
    categories.forEach(category => {
      if (!category.startsWith('N6_')) filteredCategories.push(category);
    });
    return filteredCategories;
  }

  private _getUniqueMainCategories(categories: LevumiItemCategory[]) {
    const mainCategories: string[] = [];
    categories.forEach(category => {
      const categoryParts = category.split('-');
      if (!categoryParts[0] || !categoryParts[1]) return;
      const mainCategory = categoryParts[0].trim();
      if (!mainCategories.includes(mainCategory)) mainCategories.push(mainCategory);
    });
    return mainCategories;
  }

  private _getUniqueSubCategoriesForMainCategory(mainCategory: string, categories: LevumiItemCategory[]) {
    const subCategories: string[] = [];
    categories.forEach(category => {
      const categoryParts = category.split('-');
      if (!categoryParts[0] || !categoryParts[1]) return;
      const mainCategoryOfCategory = categoryParts[0].trim();
      const subCategoryOfCategory = categoryParts[1].trim();
      if (mainCategoryOfCategory === mainCategory && !subCategories.includes(subCategoryOfCategory))
        subCategories.push(subCategoryOfCategory);
    });
    return subCategories;
  }

  private _validateDistractors(distractorString: string) {
    const arr = distractorString.split(',');
    if (arr.some(item => item.length === 0)) {
      this._errorMsg.next('Kein Ablenker darf leer sein!');
      return false;
    }
    return true;
  }

  public override async cancel() {
    if (this._itemDirty.value && this.levumiGermanItem.value._id) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      this.levumiGermanItem.value.revert();
    }
    await super.cancel();
  }
}
